'use client'
import { pageService } from '@/services/pageServices';
import { ChevronRightIcon, Search, X } from 'lucide-react';
import Image from 'next/image';
import Link from "next/link";
import React, { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useRecaptcha } from '@/utils/useRecaptcha';
import GumletImage from '@/utils/GumletSrcSet';

const SearchModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [suggestions, setSuggestions] = useState(['React', 'Vue', 'Angular', 'Svelte', 'Next.js', 'Nuxt', 'Remix', 'Solid']);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchFilters, setSearchFilters] = useState({
    q: '',
    FORMAT: '',
    CATEGORIES: '',
    TAGS: '',
    BRAND: '',
    AUTHORS: '',
  });

  const router = useRouter();
  const searchParams = useSearchParams();
  const query = searchParams?.get('q') || '';
  const [topSearch, setTopSearch] = useState(null);
  
  // Initialize reCAPTCHA - will work when script is loaded (only when modal is open)
  const { executeRecaptcha } = useRecaptcha();

  useEffect(() => {
    if (isOpen && searchParams) {
      setSearchFilters({
        q: searchParams.get('q') || '',
        FORMAT: '',
        CATEGORIES: '',
        TAGS: '',
        BRAND: '',
        AUTHORS: '',
      });
    }
  }, [isOpen, searchParams]);

  const fetchSuggestions = async (query: string) => {
    if (!query) return setSuggestions([]);
    try {
      setIsLoadingSuggestions(true);
      
      // Get reCAPTCHA token for suggestions (only if script is loaded)
      const recaptchaToken = await executeRecaptcha('search');
      
      const resData = await pageService.getSearchKeywordBasedSuggestionList(
        query || "", 
        process.env.NEXT_PUBLIC_API_BASE_URL,
        recaptchaToken
      );

      setSuggestions(resData?.data || []);
    } catch (err) {
      console.error('Error fetching suggestions', err);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };
  const debounce = (func: Function, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }


  const debouncedFetch = useRef(
    debounce((text: string) => {
      fetchSuggestions(text);
    }, 300)
  ).current;

  useEffect(() => {
    debouncedFetch(searchFilters.q);
  }, [searchFilters.q]);

  const fetchCategoryData = async () => {
    let query = 'limit=10';
    try {
      // Get reCAPTCHA token for category data (only if script is loaded)
      const recaptchaToken = await executeRecaptcha('search');
      
      const categoryData = await pageService.getTagsCategoryBasedOnSearchKeyword(
        query || "", 
        process.env.NEXT_PUBLIC_API_BASE_URL,
        recaptchaToken
      );
      setTopSearch(categoryData?.data)
    } catch (error) {
      console.error('Failed to fetch category data:', error);
    }
  };

  const onSearch = () => {
    const params = new URLSearchParams();

    Object.entries(searchFilters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    onClose();
    router.push(`/search?${params.toString()}`);
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategoryData();
      // Show reCAPTCHA badge when modal opens
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        (badge as HTMLElement).style.visibility = 'visible';
        (badge as HTMLElement).style.opacity = '1';
      }
    } else {
      // Hide reCAPTCHA badge when modal closes (if not on search page)
      // RecaptchaBadgeController will also handle this on navigation
      const isSearchPage = window.location.pathname === '/search';
      if (!isSearchPage) {
        const badge = document.querySelector('.grecaptcha-badge');
        if (badge) {
          (badge as HTMLElement).style.visibility = 'hidden';
          (badge as HTMLElement).style.opacity = '0';
        }
      }
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  return (
    <>
      {/* Load Google reCAPTCHA v3 script ONLY when modal is open (not on every page) */}
      {isOpen && process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
          strategy="afterInteractive"
        />
      )}
      
      <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/20 backdrop-blur-sm" onClick={onClose}>
        <div
          className="relative w-full max-w-4xl mx-4 my-6 bg-white rounded-lg shadow-xl flex flex-col" style={{ maxHeight: 'calc(100vh - 32px)' }}

          onClick={(e) => e.stopPropagation()}
        >
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 bg-background  z-[101] rounded-md shadow-lg "
         aria-label="Close">

          <X className='w-6 h-6 md:m-1 text-inkBlack' />
        </button>
        {/* <div className='overflow-hidden'> */}
        <div className="relative p-4 md:p-6 lg:p-8">
          <input
            ref={inputRef}
            value={searchFilters.q}
            onChange={(e) => setSearchFilters({ ...searchFilters, q: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearch();
              }
            }}
            type="text"
            placeholder="Search..."
            className="w-full px-4 ps-10 pr-28 py-2 border border-gray-300 border-t-0 border-l-0 border-r-0 text-base md:text-lg focus:outline-none"
          />
          <button className='absolute right-5 md:right-10 top-1/2 bg-background flex items-center text-borderGray gap-x-1 font-medium uppercase -translate-y-1/2 text-sm md:text-base'
            onClick={onSearch}
           aria-label="Search">
            Search <ChevronRightIcon className='w-4 h-4 md:w-6 md:h-6 text-borderGray' />
          </button>
          <Search className='absolute left-5 md:left-10 top-1/2 -translate-y-1/2 text-borderGray w-4 h-4 md:w-5 md:h-5' />
        </div>
        {/* Close Button */}

        {/* Modal Content Container - Added overflow-y-auto here */}
        <div className="flex-1 min-h-0 px-4 pb-4 md:px-6 md:pb-6 lg:px-8 lg:pb-8 overflow-y-auto custom-scrollbar">
          {/* Search Input */}

          <div className={`grid gap-6 md:gap-8 ${searchFilters?.q && suggestions.length > 0 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            {searchFilters?.q && suggestions.length > 0 ? (
              <div>
                <h2 className="text-xl md:text-2xl font-medium mb-4">Suggestions</h2>
                <div className="space-y-3 md:space-y-4">
                  {suggestions?.length > 0 && suggestions?.map((read: any, index: number) => (
                    <Link key={index} href={`${read.post_full_path}`} target='_blank' className="flex items-center space-x-3 border-b md:border-b-0 border-borderGray last:border-b-0 pb-3 group cursor-pointer"
                    prefetch={false}
                    >
                      <GumletImage
                        imageUrl={read.image}
                        fallbackSrc="/assets/images/dummy.jpg"
                        alt={read.post_title || ""}
                        className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover"
                        width={64}
                        height={64}
                      />
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">{read.name}</p>
                        <h3 className="font-semibold text-sm md:text-md">{read.post_title}</h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Popular Reads Section */}
                <div className={`${suggestions.length > 0 ? 'md:border-r md:pr-4 lg:pr-6' : ''}`}>
                  {/* <div className='md:border-r md:pr-4 lg:pr-6'> */}
                  <h2 className="text-xl md:text-2xl font-medium mb-4">Popular Reads</h2>
                  <div className="space-y-3 md:space-y-4">
                    {(topSearch as any)?.postsTop?.length > 0 && (topSearch as any)?.postsTop?.map((read: any, index: number) => (
                      <Link key={index} href={`${read.post_full_path}`} target='_blank' className="flex items-start space-x-3 border-b md:border-b-0 border-borderGray last:border-b-0 pb-3 group cursor-pointer"
                      prefetch={false}
                      >
                        <GumletImage
                          imageUrl={read.image}
                          fallbackSrc="/assets/images/dummy.jpg"
                          alt={read.post_title || ""}
                          className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover"
                          width={64}
                          height={64}
                        />
                        <div>
                          <p className="text-xs md:text-sm text-gray-500">{read.name}</p>
                          <h3 className="font-semibold text-sm md:text-md">{read.post_title}</h3>
                        </div>
                      </Link>
                    ))}
                  </div>

                </div>

                {/* Right Side Content */}
                <div>
                  {/* Popular Keywords */}
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-medium mb-3 md:mb-4">Popular Keywords</h2>
                    <div className="flex flex-wrap gap-2">
                      {(topSearch as any)?.options?.length > 0 && (topSearch as any)?.options?.map((keyword: any, index: number) => (
                        <Link key={index} href={`${keyword.slug_url}`} target='_blank' className="flex items-center space-x-3 pb-3 group cursor-pointer"
                        prefetch={false}
                        >
                          <span
                            key={index}
                            className="border border-borderGray px-4 py-1 rounded-lg text-sm md:text-lg text-borderGray cursor-pointer"
                            onClick={() => {
                              if (keyword.type === "category") {
                                setSearchFilters((prev) => ({
                                  ...prev,
                                  q: keyword.label,
                                  TAGS: '',
                                  FORMAT: '',
                                  CATEGORIES: keyword.label,
                                  categorySlug: keyword.slug
                                }));
                              } else if (keyword.type === "tag") {
                                setSearchFilters((prev) => ({
                                  ...prev,
                                  q: keyword.label,
                                  CATEGORIES: '',
                                  FORMAT: '',
                                  TAGS: keyword.label,
                                  tagSlug: keyword.slug
                                }));
                              }
                              inputRef.current?.focus();
                              navigator.clipboard.writeText(keyword.label);
                            }}
                          >
                            {keyword?.label}
                          </span></Link>
                      ))}
                    </div>
                  </div>

                  {/* Popular Videos */}
                  <div>
                    <h2 className="text-xl md:text-2xl font-medium mb-3 md:mb-4">Popular Videos</h2>
                    <div className="space-y-3 md:space-y-4">
                      {(topSearch as any)?.postsTopVideos?.length > 0 && (topSearch as any)?.postsTopVideos?.map((video: any, index: number) => (
                        <Link key={index} href={`${video.post_full_path}`} target='_blank' className="flex items-center space-x-3 border-b md:border-b-0 border-borderGray last:border-b-0 pb-3 md:pb-0 group cursor-pointer"
                        prefetch={false}
                        >
                          <div className="relative">
                            <GumletImage
                              imageUrl={video?.image}
                              fallbackSrc="/assets/images/dummy.jpg"
                              alt={video?.post_title}
                              className="w-20 h-12 md:w-24 md:h-16 rounded-lg object-cover"
                              width={64}
                              height={64}
                            />
                            <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
                              {video?.duration}
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 md:h-8 md:w-8 text-white bg-black/50 rounded-full p-1 md:p-2"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                          <h3 className="font-semibold text-sm md:text-md flex-1">{video?.post_title}</h3>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </>)}

          </div>
        </div>
        {/* </div> */}
        </div>
      </div>
    </>
  );
};

export default SearchModal;